/**
 * `GET /v0/goods/popular-today` → the rows the section draws.
 *
 * NO DEMO FALLBACK HERE, DELIBERATELY. The other home sections substitute
 * placeholder cards when their endpoint says nothing, and `useDemoCardWatch`
 * exists because that substitution hid a silent backend. This block is
 * segmented and one of its segments is personal: a placeholder row titled "For
 * you" would claim a taste profile that was never read. A segment with no
 * drawable product is dropped, and a block with no segments left renders
 * nothing at all.
 *
 * A PRODUCT WITHOUT AN IMAGE IS NOT A CARD. The endpoint used to return none at
 * all — it asked the index for a `vendors` object that does not exist — so the
 * guard is not hypothetical, and it is the reason this section can be trusted
 * to render only what it actually received.
 */

import type {
  PopularTodayProduct,
  PopularTodayResponse,
  PopularTodaySegment,
} from '@/features/recommendations/api/popularToday'
import { formatPriceWithCurrency } from '@/utils/currencyFormat'

export const FAIR_HOME_POPULAR_TODAY_DEFAULT_AUDIENCE = 'woman'

/** Above-fold cards in the first row — eager + fetchpriority=high. */
export const FAIR_HOME_POPULAR_TODAY_LCP_PRIORITY_COUNT = 4

/** The personal segment's id, as the contract names it. */
export const FAIR_HOME_POPULAR_TODAY_PERSONAL_SEGMENT = 'for_you'

export interface FairHomePopularTodayCard {
  id: number
  variantId: number
  brand: string
  title: string
  price: string
  images: string[]
  isNew: boolean
}

export interface FairHomePopularTodayRow {
  id: string
  title: string
  personal: boolean
  cards: FairHomePopularTodayCard[]
}

function images(product: PopularTodayProduct): string[] {
  const urls = product.image_urls ?? []
  const main = product.main_image_url
  const ordered = main ? [main, ...urls.filter((url) => url !== main)] : urls
  return ordered.filter((url): url is string => Boolean(url))
}

function toCard(product: PopularTodayProduct): FairHomePopularTodayCard | null {
  const gallery = images(product)
  if (!gallery.length) return null
  return {
    id: product.product_id,
    variantId: product.product_variant_id,
    brand: product.brand_name ?? '',
    title: product.product_name ?? '',
    price: formatPriceWithCurrency(product.min_price ?? 0, product.currency ?? ''),
    images: gallery,
    isNew: Boolean(product.is_new_collection),
  }
}

/** The row's heading, in the locale the shopper is reading. */
export function popularTodayTitle(segment: PopularTodaySegment, locale: string): string {
  const russian = locale.toLowerCase().startsWith('ru')
  return (russian ? segment.title_ru : segment.title) || segment.title || ''
}

export function resolveFairHomePopularToday(
  response: PopularTodayResponse | null | undefined,
  locale = 'en',
): FairHomePopularTodayRow[] {
  const segments = response?.segments ?? []
  const rows: FairHomePopularTodayRow[] = []
  for (const segment of segments) {
    const cards = (segment.products ?? [])
      .map(toCard)
      .filter((card): card is FairHomePopularTodayCard => card !== null)
    if (!cards.length) continue
    rows.push({
      id: segment.id,
      title: popularTodayTitle(segment, locale),
      personal: segment.id === FAIR_HOME_POPULAR_TODAY_PERSONAL_SEGMENT,
      cards,
    })
  }
  return rows
}
